<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import type {
    UsageProfilePublicHoliday,
    UsageProfileSchoolHoliday,
} from "@modules/usage-profile/types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            heading1: "Exceptions",
            schoolholiday: "Vacances scolaires",
            publicholiday: "Jours fériés",
            close: "Fermé",
            open: "Ouvert",
            zone_a: "Zone A",
            zone_b: "Zone B",
            zone_c: "Zone C",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Vacances scolaires (zone + fermé) */
        schoolHoliday?: UsageProfileSchoolHoliday
        /** Jours fériés (fermé) */
        publicHoliday?: UsageProfilePublicHoliday
    }>(),
    {
        schoolHoliday: () => ({ ferme: true, zone: "A" }),
        publicHoliday: () => ({ ferme: true }),
    }
)

const emit = defineEmits<{
    "input:schoolholiday": [value: UsageProfileSchoolHoliday]
    "input:publicHoliday": [value: UsageProfilePublicHoliday]
}>()

// ================================
// STATE
// ================================
/** Vacances scolaires : ouvert (true) ou fermé (false) */
const selectedSchoolHoliday = ref(!props.schoolHoliday.ferme)
/** Jours fériés : ouvert (true) ou fermé (false) */
const selectedPublicHoliday = ref(!props.publicHoliday.ferme)
/** Zone scolaire sélectionnée (A, B, C) */
const filterValue = ref(props.schoolHoliday.zone)

// ================================
// COMPUTED (options zone)
// ================================
/** Options de zone scolaire pour le select */
const getZoneOptions = [
    {
        label: t("zone_a"),
        value: "A",
    },
    {
        label: t("zone_b"),
        value: "B",
    },
    {
        label: t("zone_c"),
        value: "C",
    },
]

// ================================
// INTERACTIONS
// ================================
/** Met à jour la config vacances scolaires (ouvert/fermé) */
function onCheckSchoolHoliday(checked: boolean): void {
    selectedSchoolHoliday.value = checked
    emit("input:schoolholiday", {
        ferme: !checked,
        zone: filterValue.value,
    })
}

/** Met à jour la config jours fériés (ouvert/fermé) */
function onCheckPublicHoliday(checked: boolean): void {
    selectedPublicHoliday.value = checked
    emit("input:publicHoliday", {
        ferme: !checked,
    })
}

/** Met à jour la zone scolaire */
function onChangeFilter(value: string): void {
    filterValue.value = value
    emit("input:schoolholiday", {
        ferme: !selectedSchoolHoliday.value,
        zone: filterValue.value,
    })
}

// ================================
// WATCHERS
// ================================
watch(
    () => props.schoolHoliday,
    (val) => {
        selectedSchoolHoliday.value = !val?.ferme
        filterValue.value = val?.zone ?? "A"
    },
    { immediate: true }
)
watch(
    () => props.publicHoliday,
    (val) => {
        selectedPublicHoliday.value = !val?.ferme
    },
    { immediate: true }
)
</script>

<template>
    <div class="usage-profile-edit-exception flex flex-col gap-4">
        <h3 class="text-lg font-semibold">
            {{ t("heading1") }}
        </h3>
        <div class="flex flex-col gap-3">
            <!-- Vacances scolaires -->
            <div
                class="flex justify-between items-center gap-4 flex-wrap p-2.5"
            >
                <p class="text-sm font-medium text-text-color">
                    {{ t("schoolholiday") }}
                </p>
                <div class="flex items-center gap-3">
                    <BaseSelect
                        :model-value="filterValue"
                        :options="getZoneOptions"
                        option-label="label"
                        option-value="value"
                        class="w-36 min-w-24"
                        @update:model-value="onChangeFilter"
                    />
                    <div class="flex items-center gap-2">
                        <BaseToggleSwitch
                            :model-value="selectedSchoolHoliday"
                            @update:model-value="
                                (v: boolean) => onCheckSchoolHoliday(v)
                            "
                        />
                        <span class="text-sm">
                            {{ selectedSchoolHoliday ? t("open") : t("close") }}
                        </span>
                    </div>
                </div>
            </div>
            <!-- Jours fériés -->
            <div
                class="flex justify-between items-center gap-4 flex-wrap p-2.5"
            >
                <p class="text-sm font-medium text-text-color">
                    {{ t("publicholiday") }}
                </p>
                <div class="flex items-center gap-2">
                    <BaseToggleSwitch
                        :model-value="selectedPublicHoliday"
                        @update:model-value="
                            (v: boolean) => onCheckPublicHoliday(v)
                        "
                    />
                    <span class="text-sm">
                        {{ selectedPublicHoliday ? t("open") : t("close") }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>
